import BaseRenderer from 'diagram-js/lib/draw/BaseRenderer';
import { getBusinessObject, is, isAny } from 'bpmn-js/lib/util/ModelUtil';
import { isLabel } from 'bpmn-js/lib/util/LabelUtil';
import { append as svgAppend, attr as svgAttr, create as svgCreate } from 'tiny-svg';
import { getModelerTemplateIcon } from './index';

const HIGH_PRIORITY = 1250;

export class ElementTemplateIconRenderer extends BaseRenderer {
    private _bpmnRenderer: any;
    private _iconProperty: any;

    static $inject = ['eventBus', 'bpmnRenderer', 'config.elementTemplateIconRenderer'];

    constructor(eventBus: any, bpmnRenderer: any, config?: any) {
        super(eventBus, HIGH_PRIORITY);

        this._bpmnRenderer = bpmnRenderer;
        this._iconProperty = config && config.iconProperty;
    }

    canRender(element: any) {
        if (isLabel(element)) {
            return false;
        }

        return !!(
            isAny(element, ['bpmn:Task', 'bpmn:Event', 'bpmn:ServiceTask']) &&
            this._getIcon(element)
        );
    }

    _getIcon(element: any) {
        return getModelerTemplateIcon(element, this._iconProperty);
    }

    drawShape(parentGfx: any, element: any, attrs: any = {}) {
        const elementType = [
            'bpmn:BoundaryEvent',
            'bpmn:EndEvent',
            'bpmn:IntermediateCatchEvent',
            'bpmn:IntermediateThrowEvent',
            'bpmn:StartEvent',
            'bpmn:ServiceTask',
            'bpmn:Task',
            'bpmn:UserTask',
            'bpmn:ManualTask'
        ].find((t) => is(element, t));

        if (!elementType || !this._bpmnRenderer.handlers[elementType]) {
            return parentGfx;
        }

        const renderer = this._bpmnRenderer.handlers[elementType];
        const gfx = renderer(parentGfx, element, { ...attrs, renderIcon: false });

        const icon = this._getIcon(element);
        if (!icon) {
            return gfx;
        }

        // `icon` may have come from either the legacy attribute or the
        // module-level registry — either way, if we got a URL we draw it.
        // The old code read `camunda:modelerTemplateIcon` a second time
        // here just to gate the draw; that's redundant now that `_getIcon`
        // returns undefined when there's nothing to render.
        if (icon) {
            const width = element.width;
            const height = element.height;

            // Clear the parent completely - remove all children rendered by base renderer
            while (parentGfx.firstChild) {
                parentGfx.removeChild(parentGfx.firstChild);
            }

            const img = svgCreate('image');
            svgAttr(img, {
                href: icon,
                width: width,
                height: height
            });

            svgAppend(parentGfx, img);

            // Add name at bottom
            const name = element.businessObject.name || '';
            if (name) {
                const label = svgCreate('text');
                label.textContent = name;
                label.setAttribute('x', String(element.width / 2));
                label.setAttribute('y', String(element.height + 15));
                label.setAttribute('text-anchor', 'middle');
                label.setAttribute('font-size', '11px');
                label.setAttribute('fill', '#000');
                svgAppend(parentGfx, label);
            }
        }

        return gfx;
    }

    destroy() {
        // Cleanup logic if needed
    }
}

export function hasEventDefinition(element: any) {
    const businessObject = getBusinessObject(element);
    return (businessObject.eventDefinitions || []).length > 0;
}
